import { useState, useEffect, forwardRef, useCallback, useRef } from "react";
import { Input } from "@tellescope/design-system";
import type { ComponentProps, ChangeEvent } from "react";

export interface DobTextInputProps extends Omit<ComponentProps<typeof Input>, "type" | "onChange"> {
  /** Callback fired when the date value changes (receives formatted MM-DD-YYYY string) */
  onChange?: (value: string) => void;
  /** The current date value (MM-DD-YYYY format, controlled) */
  value?: string;
  /** The default date value (MM-DD-YYYY format, uncontrolled) */
  defaultValue?: string;
}

/**
 * A date of birth text input component with MM-DD-YYYY formatting.
 *
 * Features:
 * - Auto-inserts dashes after MM and DD as user types
 * - Converts `/` to `-` for user convenience
 * - Filters non-numeric characters (except dashes)
 * - Returns formatted MM-DD-YYYY string via onChange callback
 * - Supports both controlled and uncontrolled usage
 * - Maintains proper cursor positioning during edits
 *
 * @param onChange - Callback fired when the value changes (receives formatted MM-DD-YYYY string)
 * @param value - The current date value for controlled usage (MM-DD-YYYY format)
 * @param defaultValue - The default date value for uncontrolled usage (MM-DD-YYYY format)
 * @param ...props - All other Input component props (except type, onChange)
 *
 * @example
 * ```tsx
 * // Controlled usage
 * <DobTextInput
 *   value={dateOfBirth}
 *   onChange={setDateOfBirth}
 *   placeholder="MM-DD-YYYY"
 * />
 *
 * // Uncontrolled usage
 * <DobTextInput
 *   defaultValue="01-15-1990"
 *   onChange={(value) => console.log('DOB:', value)}
 * />
 * ```
 */
export const DobTextInput = forwardRef<HTMLInputElement, DobTextInputProps>(
  ({ onChange, value, defaultValue, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    /**
     * Formats raw digits into MM-DD-YYYY format.
     * Inserts dashes after the 2nd and 4th digits.
     * Adds trailing dash when MM or DD group is complete.
     */
    const formatDob = useCallback((digits: string): string => {
      if (!digits) return "";

      let formatted = "";
      for (let i = 0; i < digits.length && i < 8; i++) {
        // Insert dash after MM (2 digits) and DD (4 digits total)
        if (i === 2 || i === 4) {
          formatted += "-";
        }
        formatted += digits[i];
      }

      // Add trailing dash after completing MM (2 digits) or DD (4 digits)
      if (digits.length === 2 || digits.length === 4) {
        formatted += "-";
      }

      return formatted;
    }, []);

    /**
     * Strips all non-numeric characters from input to get raw digits.
     * Limits to 8 digits (MMDDYYYY).
     */
    const stripFormatting = useCallback((formatted: string): string => {
      return formatted.replace(/\D/g, "").slice(0, 8);
    }, []);

    /**
     * Initialize display value based on controlled/uncontrolled usage.
     */
    const [displayValue, setDisplayValue] = useState<string>(() => {
      if (value !== undefined) {
        return formatDob(stripFormatting(value));
      }
      if (defaultValue !== undefined) {
        return formatDob(stripFormatting(defaultValue));
      }
      return "";
    });

    /**
     * Sync display value when controlled value changes.
     */
    useEffect(() => {
      if (value !== undefined) {
        setDisplayValue(formatDob(stripFormatting(value)));
      }
    }, [value, formatDob, stripFormatting]);

    /**
     * Handle input changes: extract digits, format, and notify parent with formatted value.
     */
    const handleInputChange = useCallback(
      (e: ChangeEvent<HTMLInputElement> | string) => {
        const inputValue = typeof e === "string" ? e : e.target.value;
        const cursorPosition = typeof e === "string" ? inputValue.length : e.target.selectionStart ?? 0;

        // Convert slashes to dashes for user convenience
        const normalizedInput = inputValue.replace(/\//g, "-");

        // Extract only digits from the input
        const digits = stripFormatting(normalizedInput);

        // Count how many digits were before the cursor
        const digitsBeforeCursor = stripFormatting(normalizedInput.slice(0, cursorPosition)).length;

        // Format the digits
        const formatted = formatDob(digits);

        // Update display value
        setDisplayValue(formatted);

        // Calculate new cursor position by finding where the Nth digit is in formatted string
        let newCursorPosition = 0;
        let digitCount = 0;
        for (let i = 0; i < formatted.length; i++) {
          const ch = formatted[i];
          if (ch !== undefined && /\d/.test(ch)) {
            digitCount++;
            if (digitCount === digitsBeforeCursor) {
              newCursorPosition = i + 1;
              break;
            }
          }
        }

        // If we've counted all digits, place cursor at end
        if (digitCount < digitsBeforeCursor || digitsBeforeCursor === digits.length) {
          newCursorPosition = formatted.length;
        }

        // Set cursor position after React updates the input
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
          }
        });

        // Call onChange with formatted value (MM-DD-YYYY)
        if (onChange) {
          onChange(formatted);
        }
      },
      [stripFormatting, formatDob, onChange]
    );

    /**
     * Handle backspace when cursor is after a dash.
     * Deletes the digit before the dash instead of just the dash.
     */
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace") {
          const lastChar = displayValue.at(-1);
          // If the last character is a dash, we need to remove the digit before it too
          if (lastChar === "-") {
            const digits = stripFormatting(displayValue);
            handleInputChange(digits.slice(0, digits.length - 1));
            e.preventDefault();
          }
        }
        // Call the original onKeyDown if provided
        props.onKeyDown?.(e);
      },
      [displayValue, stripFormatting, handleInputChange, props]
    );

    return (
      <Input
        {...props}
        ref={(node) => {
          inputRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        type="text"
        inputMode="numeric"
        maxLength={10} // MM-DD-YYYY = 10 characters
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
    );
  }
);

DobTextInput.displayName = "DobTextInput";
